import React, { memo } from 'react';
import { useDispatch } from 'react-redux';
import { Tag } from 'modules/tag/Tag';
import { View, Title, InlineLink, SmallText, Text } from 'modules/styled';
import { goToUrl, goToPost } from 'modules/navigation/navigation.actions';
import history from 'modules/navigation/history';
import { usePostUrl, usePostComunity } from './hooks';

interface TitleElProps {
  post: any;
  noLink?: boolean;
  link?: any;
  c?: string;
  singlePost?: boolean;
}

interface PostTitleProps {
  link: any;
  post: any;
  children?: JSX.Element;
  preview: boolean;
  singlePost: boolean;
  noLink: boolean;
  title: string;
  mobile: boolean;
}

export default memo(PostTitle);

function PostTitle({
  children,
  post,
  link,
  title,
  noLink,
  singlePost,
  preview,
  mobile,
}: PostTitleProps) {
  const postUrl = usePostUrl(post);

  if (!post) return null;

  const c = mobile ? 'textOverlay' : undefined;
  const titleLines = preview && mobile ? 2 : 3;

  const renderCommentLink = (post.commentCount && postUrl) || post?.tags?.length;

  return (
    <View
      webContainerSx={{ flex: 1 }}
      sx={{ flex: 1, justifyContent: ['center', 'flex-start'] }}
    >
      <View>
        <TitleEl
          c={c}
          titleLines={titleLines}
          title={title}
          post={post}
          noLink={noLink}
          singlePost={singlePost}
        />
        <DomainEl singlePost={singlePost} noLink={noLink} link={link} post={post} c={c} />
      </View>
      {renderCommentLink ? (
        <Text numberOfLines={1}>
          <CommentEl post={post} c={c} noLink={noLink} />
          <TagEl post={post} c={c} noLink={noLink} />
        </Text>
      ) : null}
      {children}
    </View>
  );
}

interface TitleProps extends TitleElProps {
  singlePost: boolean;
  title: string;
  titleLines: number;
}

export const TitleEl = ({
  post,
  noLink,
  title,
  singlePost,
  c,
  titleLines,
}: TitleProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const postUrl = usePostUrl(post);

  if (!title) return null;
  return (
    <InlineLink
      to={post.url}
      external
      target={'_blank'}
      disabled={noLink}
      rel={'ugc nofollow'}
      webAction={() => history.push(postUrl)}
      nativeAction={() => {
        singlePost
          ? dispatch(goToUrl(post.url))
          : dispatch(goToUrl(post.url)) && dispatch(goToPost(post));
      }}
    >
      <Title
        numberOfLines={titleLines}
        sx={{
          color: c,
        }}
      >
        {title.replace(/&amp;/g, '&')}
      </Title>
    </InlineLink>
  );
};

export const DomainEl = ({
  singlePost,
  link,
  post,
  noLink,
  c,
}: TitleElProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const postUrl = usePostUrl(post);

  const hasAuthor = link && link.articleAuthor && link.articleAuthor.length;
  const authorEl = hasAuthor ? (
    <SmallText sx={{ mr: 0.5 * 8.001, color: c || 'grey' }}>
      {' '}
      • {link.articleAuthor.join(', ')}
    </SmallText>
  ) : null;

  if (!link?.domain && !authorEl) return null;

  return (
    <Text numberOfLines={1} sx={{ mt: 2.001 }}>
      {link?.domain && (
        <InlineLink
          to={post.url}
          target="_blank"
          external
          disabled={!postUrl || noLink}
          hu
          c={c || 'blue'}
          rel={'ugc nofollow'}
          webAction={() => history.push(postUrl)}
          nativeAction={() => {
            singlePost
              ? dispatch(goToUrl(post.url))
              : dispatch(goToUrl(post.url)) && dispatch(goToPost(post));
          }}
        >
          <SmallText sx={{ color: c || 'blue' }}>
            {link.domain && `${link.domain}\u00A0\u2197\uFE0E`}
          </SmallText>
        </InlineLink>
      )}
      {authorEl}
    </Text>
  );
};

export const CommentEl = ({ post, noLink, c }: TitleElProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const postUrl = usePostUrl(post);

  if (!post.commentCount) return null;

  return (
    <InlineLink
      to={postUrl}
      hu
      disabled={noLink}
      c={c || 'blue'}
      nativeAction={() => dispatch(goToPost(post))}
    >
      <SmallText sx={{ color: c || 'blue' }}>
        {post.commentCount} Comment{post.commentCount > 1 ? 's' : ''}
        &nbsp;&nbsp;
      </SmallText>
    </InlineLink>
  );
};

export const TagEl = ({ noLink, c, post }: TitleElProps): JSX.Element | null => {
  const community = usePostComunity(post);
  const tags = post?.tags;
  if (!tags?.length) return null;

  return tags.map((tag: string, i: number) => (
    <Tag
      name={tag}
      key={tag + i}
      community={community || (post.data && post.data.community)}
      noLink={noLink}
      c={c || 'blue'}
    />
  ));
};
